import React, { useMemo } from "react";
import SavedPaymentMethods from "../../paymentMethods/SavedPaymentMethods";
import { usePaymentMethods, usePayment, useTicket } from "../../../store/Store";
import { paymentAuthorizedStatus } from "../../general/constants";
import { Trans, useTranslation } from "react-i18next";
import Currency from "../../general/Currency";
import PaymentMethodToggle from "../../paymentMethods/PaymentMethodToggle";
import CreditCardForm from "../forms/CreditCardForm";

const PaymentProcessor = () => {
  const { data: ticketData } = useTicket();
  const { showSavedPaymentMethods } = usePaymentMethods();
  const { isProcessing: processingPayment } = usePayment();
  const { t } = useTranslation();

  const client = useMemo(() => ticketData?.resource?.client, [ticketData]);

  // Check if there's already existing payment asscoiated with the access token
  // Only consider successful payments, not declined or failed ones
  const alreadyProcessedPaymentAmount = useMemo(() => {
    return ticketData?.resource?.payment_items?.reduce((acc, item) => {
      // if the ticket was fully paid, consider all payment items, regardless of the access token they are associated with
      const isAssociatedPaymentItem =
        ticketData?.resource?.balance_due !== 0
          ? !!ticketData?.access_token?.data?.transactions?.includes(
              item?.payment?.id,
            )
          : true;
      if (
        isAssociatedPaymentItem &&
        item?.payment?.status === paymentAuthorizedStatus &&
        item?.payment?.code_type !== "GiftCard"
      ) {
        return acc + item?.payment?.amount;
      }
      return acc;
    }, 0);
  }, [ticketData]);

  if (processingPayment) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-black">{t("payment.processing")}</p>
        </div>
      </div>
    );
  }

  // If there's already a payment with the same amount, show a message instead of payment forms
  if (alreadyProcessedPaymentAmount) {
    return (
      <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50 min-h-50">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-green-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              {t("payment.already_processed")}
            </h3>
            <p className="text-sm text-green-700 mt-2">
              <Trans
                i18nKey="payment.already_processed_details"
                components={{
                  amount: <Currency value={alreadyProcessedPaymentAmount} />,
                }}
              />
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (
    !ticketData?.resource?.payment_account ||
    ticketData?.resource?.balance_due <= 0
  ) {
    return null;
  }

  // Otherwise, show the appropriate payment form based on payment account type
  return (
    <>
      {client && !client?.walk_in && <PaymentMethodToggle />}
      {showSavedPaymentMethods && client && !client?.walk_in ? (
        <SavedPaymentMethods />
      ) : (
        <CreditCardForm />
      )}
    </>
  );
};

export default PaymentProcessor;
